import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import type { IconName } from '../components/ui/Icon';

/**
 * Amministrazione v2: le sei pagine, i loro indirizzi e la traduzione dei
 * vecchi `/admin?tab=…`.
 *
 * Fonte unica per Sidebar (sottovoci), Situazione (blocco "Le altre pagine"
 * del telefono), redirect dei vecchi link e link sparsi nel resto dell'app.
 * Specifica: os-planning/amministrazione-v2/SPEC.md (Architettura) e
 * r-proposta-claude.md § 1.3.
 *
 * Le viste dentro una pagina stanno in `?vista=` (il valore predefinito non
 * compare nell'URL, come fa `useUrlState`).
 */

export type AdminPageKey = 'situazione' | 'calendario' | 'fatture' | 'spese' | 'fisco' | 'debiti' | 'dipendenti' | 'banca';

export interface AdminPageDef {
  key: AdminPageKey;
  to: string;
  label: string;
  icon: IconName;
  /** Riga sotto il nome, nel blocco "Le altre pagine" del telefono. */
  sub: string;
  /** Livello minimo sul modulo AMMINISTRAZIONE (default READ). */
  minLevel?: PermissionLevel;
  /**
   * Fuori dal menu (16/10/2026: Situazione e Fisco, sostituite da Calendario e
   * Debiti col fisco). La pagina esiste ancora: i vecchi link continuano ad aprirla.
   */
  nascosta?: boolean;
}

/**
 * `sub` segue la regola etichette (SPEC, 24/9): letta da sola, senza il nome
 * della pagina accanto, deve dire cosa c'è dentro. Niente "documenti", niente
 * "da controllare": le cose con il loro nome.
 */
export const ADMIN_PAGES: AdminPageDef[] = [
  { key: 'calendario', to: '/admin/calendario', label: 'Calendario dei pagamenti', icon: 'calendar', sub: 'Ogni pagamento sul suo giorno, con il saldo previsto e le cose da fare' },
  { key: 'debiti',     to: '/admin/debiti',     label: 'Debiti col fisco', icon: 'landmark', sub: 'Cartelle, avvisi e piani a rate: cosa è rateizzato e cosa no' },
  { key: 'fatture',    to: '/admin/fatture',    label: 'Fatture',    icon: 'invoice', sub: 'Fatture da incassare dai clienti, fatture emesse e preventivi' },
  { key: 'spese',      to: '/admin/spese',      label: 'Spese',      icon: 'receipt', sub: 'Fatture dei fornitori e spese fisse da pagare' },
  { key: 'situazione', to: '/admin/situazione', label: 'Situazione', icon: 'wallet',  sub: 'Soldi in banca, incassi e pagamenti dei prossimi mesi', nascosta: true },
  { key: 'fisco',      to: '/admin/fisco',      label: 'Fisco',      icon: 'percent', sub: 'Calendario delle tasse, F24 e IVA da versare', nascosta: true },
  { key: 'dipendenti', to: '/admin/dipendenti', label: 'Dipendenti', icon: 'user',    sub: 'Stipendi e TFR delle persone in busta paga', minLevel: PermissionLevel.ADMIN },
  { key: 'banca',      to: '/admin/banca',      label: 'Banca',      icon: 'swap',    sub: 'Movimenti del conto e bonifici da abbinare alle fatture' },
];

/**
 * L'utente vede questa pagina? READ sul modulo AMMINISTRAZIONE per tutte,
 * ADMIN per Dipendenti (stipendi e compensi: SPEC, riga Dipendenti).
 */
export function canSeeAdminPage(
  user: { permissions: Array<{ module: string; level: string }> } | null | undefined,
  page: AdminPageKey | AdminPageDef,
): boolean {
  if (!user) return false;
  const def = typeof page === 'string' ? ADMIN_PAGES.find((p) => p.key === page) : page;
  if (!def) return false;
  const lvl = (user.permissions.find((p) => p.module === ModuleKey.AMMINISTRAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
  return hasAtLeast(lvl, def.minLevel ?? PermissionLevel.READ);
}

/** La prima pagina dell'Amministrazione: ci porta /admin (dal 16/10/2026 il calendario, prima la Situazione). */
export const ADMIN_HOME = '/admin/calendario';

export const ADMIN_PATH: Record<AdminPageKey, string> = Object.fromEntries(
  ADMIN_PAGES.map((p) => [p.key, p.to]),
) as Record<AdminPageKey, string>;

/** Le viste di ogni pagina: la prima è la predefinita. */
export const FATTURE_VISTE = ['incassare', 'documenti', 'preventivi'] as const;
export const SPESE_VISTE = ['dapagare', 'fisse', 'pagate'] as const;
export const FISCO_VISTE = ['scadenze', 'f24', 'iva', 'imposte', 'rate', 'altri'] as const;
/**
 * Banca (26/10/2026): la prima vista, quella senza `?vista=`, è la pagina dei movimenti, come
 * l'estratto conto di un'app della banca. Prima era "Bonifici da abbinare": adesso si apre dalla
 * riga "da fare" della pagina o dal menu ⋯ (`?vista=controllare`). I link vecchi con
 * `?vista=movimenti` aprono la stessa pagina.
 */
export const BANCA_VISTE = ['movimenti', 'controllare', 'commercialista'] as const;

export type FattureVista = (typeof FATTURE_VISTE)[number];
export type SpeseVista = (typeof SPESE_VISTE)[number];
export type FiscoVista = (typeof FISCO_VISTE)[number];
export type BancaVista = (typeof BANCA_VISTE)[number];

/**
 * Le etichette a schermo delle viste (le linguette `page-tabs`). Le chiavi di
 * `?vista=` restano quelle sopra: cambiano solo le parole.
 *
 * REGOLA ETICHETTE (SPEC, 24/9): ogni etichetta nomina la cosa che contiene.
 * Chi legge solo la linguetta, senza il resto della pagina, deve capire di
 * cosa si parla: "Bonifici da abbinare", non "Da controllare"; "Spese già
 * pagate", non "Pagate". La tabella dei nomi sta in SPEC.md.
 *
 * Le pagine (FatturePage, SpesePage, FiscoPage, BancaPage) e i link che
 * nominano una vista leggono da qui: le parole non si riscrivono a mano.
 * Le viste di Fisco hanno gli stessi nomi dei gruppi di tasse
 * (TAX_GROUP_LABEL di @masamune/shared), che compaiono anche nelle righe.
 */
export const VISTA_LABEL: {
  fatture: Record<FattureVista, string>;
  spese: Record<SpeseVista, string>;
  fisco: Record<FiscoVista, string>;
  banca: Record<BancaVista, string>;
} = {
  fatture: {
    incassare: 'Fatture da incassare',
    // Proposta di Lorenzo (24/9): era "Documenti di vendita".
    documenti: 'Fatture emesse da noi',
    preventivi: 'Preventivi',
  },
  spese: {
    dapagare: 'Spese da pagare',
    fisse: 'Spese fisse',
    pagate: 'Spese già pagate',
  },
  fisco: {
    scadenze: 'Calendario delle tasse',
    f24: 'F24 dei dipendenti',
    iva: 'IVA',
    imposte: 'Imposte della società (IRES e IRAP)',
    rate: 'Debiti a rate',
    altri: 'Altri versamenti al fisco',
  },
  banca: {
    controllare: 'Bonifici da abbinare',
    movimenti: 'Movimenti del conto',
    commercialista: 'Da comunicare al commercialista',
  },
};

/**
 * Costruisce un indirizzo di pagina con i suoi parametri. I valori vuoti non
 * entrano, e nemmeno la vista predefinita: l'URL resta pulito.
 */
export function adminHref(page: AdminPageKey, params: Record<string, string | null | undefined> = {}): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  const defaults: Partial<Record<AdminPageKey, string>> = {
    fatture: FATTURE_VISTE[0], spese: SPESE_VISTE[0], fisco: FISCO_VISTE[0], banca: BANCA_VISTE[0],
  };
  if (sp.get('vista') && sp.get('vista') === defaults[page]) sp.delete('vista');
  const qs = sp.toString();
  return `${ADMIN_PATH[page]}${qs ? `?${qs}` : ''}`;
}

/** Scorciatoie per i link che si ripetono nell'app. */
export const adminLinks = {
  /** Una fattura da incassare, cercata per numero. */
  fattura: (numero?: string | null) => adminHref('fatture', { q: numero ?? undefined }),
  documenti: () => adminHref('fatture', { vista: 'documenti' }),
  preventivi: () => adminHref('fatture', { vista: 'preventivi' }),
  spese: () => adminHref('spese'),
  fornitori: () => adminHref('spese', { per: 'fornitore' }),
  /**
   * Le tasse (26/10/2026): Fisco è fuori dal menu dal 16/10, quindi il link porta alla bacheca
   * "Debiti col fisco". Il nome resta `fisco` perché lo usano altri file.
   */
  fisco: () => ADMIN_PATH.debiti,
  banca: () => adminHref('banca'),
  movimenti: () => adminHref('banca', { vista: 'movimenti' }),
};

/**
 * `/admin?tab=…` → indirizzo nuovo, con i parametri tradotti (proposta § 1.3).
 * Restituisce null se nell'URL non c'è un `tab` (si resta sulla prima pagina, il calendario).
 *
 * Serve per sempre: le notifiche già salvate nel database portano ancora i
 * vecchi link (es. `link: '/admin?tab=incassi'`).
 * Dal 26/10/2026 nessun vecchio link porta più a Situazione o a Fisco (fuori dal menu dal
 * 16/10): la panoramica va al Calendario dei pagamenti, il fisco alla bacheca dei debiti.
 */
export function legacyAdminUrl(search: URLSearchParams | string): string | null {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
  const tab = sp.get('tab');
  if (!tab) return null;
  const pick = (...keys: string[]) => Object.fromEntries(keys.map((k) => [k, sp.get(k) ?? undefined]));
  // "Anni scorsi" era un interruttore di tutta la pagina: resta acceso dove
  // esiste ancora il filtro "Anche prima del 2026".
  const storico = sp.get('storico') === '1' ? '1' : undefined;

  switch (tab) {
    case 'panoramica':
      return ADMIN_HOME;
    case 'incassi':
      // Lo stato (aperte, incassate, tutte, cestinate) resta un filtro della
      // vista "Da incassare", che lo legge ancora da `status`.
      return adminHref('fatture', { ...pick('q', 'status'), storico });
    case 'uscite': {
      const pagate = sp.get('status') === 'pagate';
      return adminHref('spese', { vista: pagate ? 'pagate' : undefined, q: sp.get('q') ?? undefined, storico });
    }
    case 'fornitori':
      return adminHref('spese', { per: 'fornitore', storico });
    case 'fisco':
      return ADMIN_PATH.debiti;
    case 'movimenti':
      return adminHref('banca', { vista: 'movimenti', ...pick('acc', 'from', 'to', 'side', 'unmatched', 'q'), storico });
    case 'riconciliazione':
      return adminHref('banca', { vista: 'controllare' });
    case 'chiuse-qui':
      return adminHref('banca', { vista: 'commercialista', q: sp.get('q') ?? undefined });
    case 'documenti': {
      const type = sp.get('type');
      if (type === 'quote') return adminHref('fatture', { vista: 'preventivi', ...pick('status', 'q'), storico });
      return adminHref('fatture', { vista: 'documenti', ...pick('type', 'status', 'q'), storico });
    }
    default:
      return ADMIN_HOME;
  }
}
